import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiClient } from '../services/apiClient';

// Tipos para el usuario
export interface UserPreferences {
  theme?: 'light' | 'dark';
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  professionalId?: string;
  specialty?: string;
  role: 'admin' | 'evaluator' | 'viewer';
  isActive: boolean;
  emailVerified: boolean;
  lastLogin?: string;
  preferences?: UserPreferences;
  createdAt: string;
  updatedAt: string;
}

// Tipo para el contexto de autenticación
interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

// Crear contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook personalizado para usar el contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Props para el provider
interface AuthProviderProps {
  children: ReactNode;
}

// Provider del contexto
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Verificar si hay token guardado al cargar la app
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (savedToken && savedUser) {
      try {
        // Verificar si el token es válido
        verifyToken(savedToken);
      } catch (error) {
        console.error('Error parsing saved user data:', error);
        logout();
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  // Función de logout
  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }, []);

  // Función para verificar si el token es válido
  const verifyToken = useCallback(async (tokenToVerify: string) => {
    try {
      // Configurar el token temporalmente para esta petición
      const response = await apiClient.get('/auth/profile', {
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`,
        },
      });

      if (response.data && response.data.user) {
        setToken(tokenToVerify);
        setUser(response.data.user);
      } else {
        // Token inválido o expirado
        console.log('Token inválido o expirado, redirigiendo al login');
        logout();
      }
    } catch (error) {
      console.error('Error verificando token:', error);
      logout();
    } finally {
      setIsLoading(false);
    }
  }, [logout]);

  // Función para verificar token periódicamente
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!token) return;

    const interval = setInterval(() => {
      verifyToken(token);
    }, 5 * 60 * 1000); // Verificar cada 5 minutos

    return () => clearInterval(interval);
  }, [token]);

  // Función de login
  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      console.log('🔄 Iniciando login...', { email });
      
      const response = await apiClient.post('/auth/login', { email, password });

      console.log('✅ Login exitoso:', response.data);
      
      // El backend retorna: { message, user, token }
      const { token: responseToken, user: responseUser } = response.data;
      
      if (!responseToken || !responseUser) {
        throw new Error('Respuesta del servidor inválida');
      }
      
      // Guardar token y usuario
      setToken(responseToken);
      setUser(responseUser);
      
      // Guardar en localStorage
      localStorage.setItem('token', responseToken);
      localStorage.setItem('user', JSON.stringify(responseUser));
      
    } catch (error: any) {
      console.error('💥 Error en login:', error);
      
      // Manejar errores de axios
      if (error.response) {
        const errorMessage = error.response.data?.error?.message || error.response.data?.message || 'Error en el login';
        throw new Error(errorMessage);
      } else if (error.request) {
        throw new Error('No se pudo conectar con el servidor');
      } else {
        throw new Error(error.message || 'Error en el login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Función para actualizar datos del usuario
  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  // Valor del contexto
  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    login,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 